import React from 'react';
import { Autocomplete } from '@material-ui/lab';
import { Box, Paper, TextField, TextFieldProps } from '@material-ui/core';
import { ICountry } from '../types/companyDetails';
import { debounce } from 'lodash';
import { fetchCountries } from '../helpers/apiCalls';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    autocomplete: {
      backgroundColor: 'var(--medium-gray)',
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      letterSpacing: '0.015625em',
      borderRadius: '4px',
      '& .MuiAutocomplete-option[data-focus="true"], .MuiAutocomplete-option:active': {
        backgroundColor: 'var(--list-hover)'
      },
      '& .MuiAutocomplete-listbox': {
        paddingBottom: 0
      },
      '& .MuiAutocomplete-option': {
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(1),
        '&:hover': {
          backgroundColor: 'var(--list-hover)'
        }
      }
    }
  })
);

type OwnProps = {
  value: string;
  onSelectOption: (value: string, savedCountry: ICountry, async?: boolean) => void;
  isRequired: boolean;
  errors: any[];
  name: string;
  onChange: (value: string) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  savedCountry?: ICountry;
};

type Props = Omit<TextFieldProps, 'value' | 'onChange'> & OwnProps;
export const CountriesListContainer: React.FC<Props> = ({
  value,
  onSelectOption,
  title,
  isRequired,
  disabled,
  errors,
  onChange,
  savedCountry,
  onBlur
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [open, setOpen] = React.useState<boolean>(false);
  const [countries, setCountries] = React.useState<ICountry[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const search = React.useCallback(
    debounce(async (search: string) => {
      try {
        setIsLoading(true);
        const response = await fetchCountries(search);
        response && setCountries(response);
      } catch (error) {
        throw error;
      } finally {
        setIsLoading(false);
      }
    }, 500),
    []
  );
  const handleInputChange = (_: React.ChangeEvent<HTMLInputElement>, newValue: string, reason: string) => {
    if (newValue !== value && reason !== 'reset') {
      onChange(newValue);
      if (newValue) {
        search(newValue);
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur(event);
    setOpen(false);
  };

  function CustomPaper({ children, ...other }: React.HTMLAttributes<HTMLElement>) {
    return (
      <Paper {...other}>
        {children}
        <Box
          onMouseDown={event => {
            event.preventDefault();
          }}
        ></Box>
      </Paper>
    );
  }

  const handleSelect = async (_: React.ChangeEvent<HTMLInputElement>, value: ICountry | null) => {
    if (value && value.name) {
      const country = { ...value };
      onSelectOption(country.name, country);
      setOpen(false);
    }
  };

  return (
    <Autocomplete
      open={open}
      forcePopupIcon={false}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      inputValue={(savedCountry?.iso2 === value && savedCountry.name) || value || ''}
      loading={isLoading}
      disabled={disabled}
      filterOptions={options => options}
      loadingText={t('form.autoComplete.loading', 'Loading...')}
      noOptionsText={t('fields.noResults', 'No results found')}
      classes={{ paper: classes.autocomplete }}
      fullWidth
      getOptionLabel={option => option.name}
      renderInput={params => (
        <TextField
          {...params}
          inputProps={{ ...params.inputProps, tabIndex: 1 }}
          variant="outlined"
          required={isRequired}
          onBlur={handleBlur}
          label={title}
          error={errors[0]}
        />
      )}
      PaperComponent={CustomPaper}
      options={countries}
    />
  );
};
